import { useCallback, useEffect, useRef, useState } from 'react';
import { AntAPI } from 'ante-react-kit';
import useDebounced from '../shared/useDebounced';
import useCustomerCache from './useCustomerCache';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 180;

function mergeById(a, b) {
    const seen = new Set();
    const out = [];
    for (const r of a) { if (r && r.id != null && !seen.has(r.id)) { seen.add(r.id); out.push(r); } }
    for (const r of b) { if (r && r.id != null && !seen.has(r.id)) { seen.add(r.id); out.push(r); } }
    return out;
}

// Lọc bỏ KH không có cả tên lẫn SĐT (record rác)
function hasIdentity(r) {
    const name = r && (r.entity_name || r.entity_full_name);
    const phone = r && r.entity_contact_phone;
    return !!(name && String(name).trim()) || !!(phone && String(phone).trim());
}

export default function useCustomerSearch({ enabled, extraConditions }) {
    const [query, setQuery] = useState('');
    const debouncedQuery = useDebounced(query, SEARCH_DEBOUNCE_MS);
    const [rows, setRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [pageIndex, setPageIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const apiRef = useRef(null);
    const reqSeqRef = useRef(0);
    if (!apiRef.current) apiRef.current = new AntAPI('entity', 'BusinessEntity');

    const { searchLocal, mergeFromServer } = useCustomerCache();

    const extraKey = JSON.stringify(extraConditions || {});

    const load = useCallback(async (pIdx, append, q, extra) => {
        if (!enabled) return;
        const seq = ++reqSeqRef.current;
        setLoading(true);
        try {
            const res = await apiRef.current.getList({
                is_active: 1,
                use_AR: 1,
                ...(extra || {}),
                searchText: q || undefined,
                ordering: [{ name: 'entity_name', type: 'asc' }],
                pageIndex: pIdx,
                pageSize: PAGE_SIZE
            });
            if (seq !== reqSeqRef.current) return;
            const serverRows = ((res && res.rows) || []).filter(hasIdentity);
            mergeFromServer(serverRows);
            setRows((prev) => append ? mergeById(prev, serverRows) : mergeById(serverRows, prev.slice(0, 20).filter(hasIdentity)));
            setTotalCount((res && res.totalCount) || 0);
        } finally {
            if (seq === reqSeqRef.current) setLoading(false);
        }
    }, [enabled, mergeFromServer]);

    // Instant local hit ngay khi query thay đổi (không đợi debounce/BE)
    useEffect(() => {
        if (!enabled) return;
        const extra = JSON.parse(extraKey);
        const local = searchLocal(query, extra, PAGE_SIZE).filter(hasIdentity);
        setRows(local);
        setPageIndex(0);
        if (!query) setTotalCount(local.length);
    }, [query, extraKey, enabled, searchLocal]);

    // BE fetch sau debounce, merge với kết quả local hiện có
    useEffect(() => {
        if (!enabled) return;
        setPageIndex(0);
        load(0, false, debouncedQuery, JSON.parse(extraKey));
    }, [debouncedQuery, extraKey, enabled, load]);

    const loadMore = useCallback(() => {
        if (loading) return;
        if (rows.length >= totalCount) return;
        const next = pageIndex + 1;
        setPageIndex(next);
        load(next, true, debouncedQuery, JSON.parse(extraKey));
    }, [loading, rows.length, totalCount, pageIndex, load, debouncedQuery, extraKey]);

    return { query, setQuery, rows, totalCount, loading, loadMore };
}
